import { useEffect, useRef, useState } from 'react';
import type { SubscriptionSnapshot } from '@tabbio/types';
import type { CancelReason } from '../lib/cancel-retention';
import {
  getCancelAnalyticsProperties,
  getCancelDestination,
} from '../lib/subscription-journey/cancel-journey';
import { trackProductEvent } from '../lib/product-analytics';

/**
 * @deprecated Drives only the pre-redesign single-sheet review
 * (`CancelPlanSheet.tsx`, `CancelPlanDialog.web.tsx`). The journey uses
 * `useCancelJourney` + `lib/subscription-journey/cancel-journey.ts`; delete
 * this hook with those two surfaces (subscription journeys Phase 2/3).
 *
 * Mount for a single review session. Never changes billing or entitlements.
 */
export function useCancelPlanFlow({
  surface,
  snapshot,
  onClose,
  onContinueToStore,
}: {
  surface: 'native' | 'web';
  snapshot: SubscriptionSnapshot | null;
  onClose: () => void;
  onContinueToStore: () => Promise<void>;
}) {
  const [reason, setReason] = useState<CancelReason | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [handoffFailed, setHandoffFailed] = useState(false);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const initialAnalytics = useRef(
    getCancelAnalyticsProperties({ surface, snapshot }),
  );
  const properties = initialAnalytics.current;

  useEffect(() => {
    alive.current = true;
    trackProductEvent(
      'subscription_cancel_flow_opened',
      initialAnalytics.current,
    );
    return () => {
      alive.current = false;
    };
  }, []);

  const close = (action = 'dismissed') => {
    if (inFlight.current) return;
    trackProductEvent('subscription_cancel_flow_closed', {
      ...properties,
      action,
      reason: reason ?? 'skipped',
    });
    onClose();
  };

  const continueToStore = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setIsRedirecting(true);
    setHandoffFailed(false);
    const event = {
      ...properties,
      destination: getCancelDestination(snapshot?.store),
      reason: reason ?? 'skipped',
    };
    trackProductEvent('subscription_cancel_store_handoff', event);
    try {
      await onContinueToStore();
      // An opened provider screen is not a cancellation or a retained member.
      trackProductEvent('subscription_cancel_store_opened', event);
      if (alive.current) onClose();
    } catch {
      trackProductEvent('subscription_cancel_store_open_failed', event);
      if (alive.current) setHandoffFailed(true);
    } finally {
      inFlight.current = false;
      if (alive.current) setIsRedirecting(false);
    }
  };

  const selectReason = (next: CancelReason | null) => {
    if (inFlight.current) return;
    setReason(next);
    trackProductEvent('subscription_cancel_reason_selected', {
      ...properties,
      reason: next ?? 'skipped',
    });
  };

  return {
    reason,
    selectReason,
    isRedirecting,
    handoffFailed,
    close,
    continueToStore,
  };
}
